import React, { useContext, useState } from "react";
import { CartContext } from "../context/CartContext";
import { Link } from "react-router-dom";
import { useCurrency } from "../context/CurrencyContext";

export default function ProductCard({ product }) {
  const { addToCart } = useContext(CartContext);
  const { format }    = useCurrency();
  
  const hasColors = product.colors && product.colors.length > 0;
  const hasSizes  = product.sizes  && product.sizes.length  > 0;

  const [selectedColor, setSelectedColor] = useState(
    hasColors ? product.colors[0] : null
  );
  const [selectedSize, setSelectedSize] = useState(null);
  const [added, setAdded] = useState(false);
  const [error, setError] = useState(false);

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (hasSizes && !selectedSize) {
      setError(true);
      setTimeout(() => setError(false), 1800);
      return;
    }
    addToCart(product, selectedSize, selectedColor?.name || null);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;1,400&family=DM+Sans:wght@300;400;500&display=swap');

        .pc-card {
          font-family:'DM Sans',sans-serif;
          background:#fdf9f6;
          border:1px solid #ede8e2;
          display:flex;
          flex-direction:column;
          transition:box-shadow 0.26s, transform 0.26s;
          position:relative;
          overflow:hidden;
        }
        .pc-card:hover {
          box-shadow:0 6px 22px rgba(0,0,0,0.08);
          transform:translateY(-2px);
        }

        /* IMAGE */
        .pc-img-wrap {
          position:relative;
          overflow:hidden;
          background:#f0ebe5;
        }
        .pc-img {
          width:100%;
          height:160px;
          object-fit:cover;
          display:block;
          transition:transform 0.48s cubic-bezier(0.25,0.46,0.45,0.94);
        }
        .pc-card:hover .pc-img { transform:scale(1.04); }

        .pc-badge {
          position:absolute;
          top:8px;left:8px;
          background:#1a1410;
          color:#fdf9f6;
          font-size:8px;
          font-weight:500;
          letter-spacing:0.15em;
          text-transform:uppercase;
          padding:3px 7px;
        }

        /* BODY */
        .pc-body {
          padding:8px 10px 10px;
          flex:1;
          display:flex;
          flex-direction:column;
          gap:5px;
        }

        .pc-name {
          font-family:'Cormorant Garamond',serif;
          font-size:15px;
          font-weight:400;
          color:#1a1410;
          line-height:1.2;
          text-decoration:none;
          display:block;
        }
        .pc-name:hover { color:#c87090; }

        .pc-price {
          font-size:12px;
          font-weight:500;
          color:#c87090;
          letter-spacing:0.02em;
          margin:0;
        }

        /* COLORS */
        .pc-colors {
          display:flex;align-items:center;gap:4px;flex-wrap:wrap;
        }
        .pc-color-label {
          font-size:9px;letter-spacing:0.12em;
          text-transform:uppercase;color:#a09890;
          margin-right:1px;
        }
        .pc-swatch {
          width:13px;height:13px;
          border-radius:50%;
          border:2px solid transparent;
          cursor:pointer;
          transition:transform 0.14s, border-color 0.14s;
          background:none;
          padding:0;
        }
        .pc-swatch:hover { transform:scale(1.15); }
        .pc-swatch.active { border-color:#1a1410; transform:scale(1.18); }
        .pc-color-name {
          font-size:10px;color:#7a6e68;font-style:italic;
        }

        /* SIZES */
        .pc-sizes { display:flex;flex-wrap:wrap;gap:4px; }
        .pc-size {
          font-size:10px;font-weight:500;
          letter-spacing:0.04em;
          padding:3px 7px;
          border:1px solid #ddd8d2;
          color:#7a6e68;
          cursor:pointer;
          transition:all 0.15s;
          background:transparent;
          font-family:'DM Sans',sans-serif;
        }
        .pc-size:hover { border-color:#1a1410;color:#1a1410; }
        .pc-size.active { background:#1a1410;color:#fdf9f6;border-color:#1a1410; }

        /* ERROR */
        .pc-error {
          font-size:10px;color:#c87090;
          font-style:italic;
          min-height:13px;margin:0;
          animation:pc-shake 0.28s ease;
        }

        /* BUTTON */
        .pc-btn {
          width:100%;
          padding:8px;
          font-family:'DM Sans',sans-serif;
          font-size:9.5px;font-weight:500;
          letter-spacing:0.15em;text-transform:uppercase;
          border:none;cursor:pointer;
          transition:background 0.2s,color 0.2s;
          margin-top:auto;
        }
        .pc-btn-default { background:#1a1410;color:#fdf9f6; }
        .pc-btn-default:hover { background:#c87090; }
        .pc-btn-added   { background:#c87090;color:#fdf9f6; }
        .pc-btn-error   { background:#f5e6ea;color:#c87090; }

        @keyframes pc-shake {
          0%,100%{transform:translateX(0)}
          25%{transform:translateX(-3px)}
          75%{transform:translateX(3px)}
        }
      `}</style>

      <div className="pc-card">

        {/* IMAGE */}
        <Link to={`/product/${product._id}`}>
          <div className="pc-img-wrap">
            <img src={product.image} alt={product.name} className="pc-img" />
            {product.isNew && <span className="pc-badge">New</span>}
          </div>
        </Link>

        <div className="pc-body">

          <Link to={`/product/${product._id}`} className="pc-name">
            {product.name}
          </Link>
{/* Auto-converts PKR price to visitor's local currency */}
          <p className="pc-price">{format(product.price)}</p>

          {/* COLOR SWATCHES */}
          {hasColors && (
            <div className="pc-colors">
              <span className="pc-color-label">Color:</span>
              {product.colors.map((color) => (
                <button
                  key={color.name}
                  className={`pc-swatch ${selectedColor?.name === color.name ? "active" : ""}`}
                  style={{ background: color.hex }}
                  title={color.name}
                  onClick={() => setSelectedColor(color)}
                />
              ))}
              {selectedColor && (
                <span className="pc-color-name">{selectedColor.name}</span>
              )}
            </div>
          )}

          {/* SIZES */}
          {hasSizes && (
            <div>
              <div className="pc-sizes">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    className={`pc-size ${selectedSize === size ? "active" : ""}`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <p className="pc-error">{error ? "Please select a size" : ""}</p>
            </div>
          )}

          {/* ADD TO CART */}
          <button
            onClick={handleAddToCart}
            className={`pc-btn ${added ? "pc-btn-added" : error ? "pc-btn-error" : "pc-btn-default"}`}
          >
            {added ? "✓ Added" : error ? "Select Size First" : "Add to Cart"}
          </button>

        </div>
      </div>
    </>
  );
}
